import React, { ChangeEvent, useCallback, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { TFunction } from "i18next";
import {
  MdAttachFile,
  MdAutoAwesome,
  MdClose,
  MdStarBorder,
} from "react-icons/md";
import { SEND_MESSAGE_TYPE } from "../../../../../constants/types";
import { ACCEPTED_FILE_TYPES } from "../../../../../constants/fileTypes";
import { CustomInput } from "../../../../atoms/CustomInput";
import { CustomButton } from "../../../../atoms/CustomButton";
import HtmlEditor, { HtmlEditorRef } from "../../../../atoms/HtmlEditor";
import { SendMessageFormValues } from "./types";
import { isHtmlEmpty } from "./helpers";
import { RICH_TEXT_LIMITS } from "../../../../../utils/richText";
import { CharacterCounter } from "../../../../atoms/CharacterCounter";

type ComposeTabProps = {
  t: TFunction;
  type: SEND_MESSAGE_TYPE;
  contentEditorRef: React.MutableRefObject<HtmlEditorRef | null>;
  attachmentInputRef: React.RefObject<HTMLInputElement>;
  attachments: File[];
  onTriggerFileSelection: () => void;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  onCorrect?: () => void;
  isCorrectingMessage: boolean;
  onSaveDefaultIntro?: () => void;
};

const ComposeTab: React.FC<ComposeTabProps> = ({
  t,
  contentEditorRef,
  attachmentInputRef,
  attachments,
  onTriggerFileSelection,
  onFileChange,
  onRemoveFile,
  onCorrect,
  isCorrectingMessage,
  onSaveDefaultIntro,
}) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<SendMessageFormValues>();

  const [editorProblem, setEditorProblem] = useState<string>();
  const handleEditorValidation = useCallback(
    (_isValid: boolean, message?: string) => setEditorProblem(message),
    []
  );
  const contentProblem = editorProblem ?? errors?.content?.message;

  const rawContent = watch("content");
  const editorIsEmpty = contentEditorRef.current?.isEmpty() ?? true;
  const isContentUndefined = typeof rawContent === "undefined";

  const showIntroSection = true;

  return (
    <div className="divide-y divide-gray-100">
      <section className="py-6 first:pt-0 last:pb-0">
        <h3 className="mb-3 text-lg font-semibold text-gray-900">
          {t("dashboard.entities.viewEntityUser.sendMsg.sections.subjectLine")}
        </h3>
        <CustomInput
          name="subject"
          control={control}
          error={errors.subject}
          helperText={errors?.subject?.message}
          placeholder={
            t(
              "dashboard.entities.viewEntityUser.sendMsg.inp1Placeholder"
            ) as string
          }
          maxLength={100}
        />
        <CharacterCounter
          currentLength={watch("subject")?.length || 0}
          maxLength={100}
          className="mt-1.5"
        />
      </section>

      {showIntroSection && (
        <section className="py-6 first:pt-0 last:pb-0">
          <h3 className="mb-3 text-lg font-semibold text-gray-900">
            {t("dashboard.entities.viewEntityUser.sendMsg.inp3Label")}
            <span className="ml-2 text-sm font-normal text-gray-400">
              {t("dashboard.entities.viewEntityUser.sendMsg.optional")}
            </span>
          </h3>
          <CustomInput
            name="intro"
            control={control}
            error={errors.intro}
            helperText={errors?.intro?.message}
            placeholder={
              t(
                "dashboard.entities.viewEntityUser.sendMsg.placeholders.greetingExample"
              ) as string
            }
          />
          <p className="mt-2 text-xs text-gray-500">
            <i>
              {t(
                "dashboard.entities.viewEntityUser.sendMsg.inp4Label"
              ).toString()}
            </i>
          </p>
          {onSaveDefaultIntro && (
            <div className="mt-2 flex justify-end">
              <CustomButton
                variant="none"
                size="xs"
                onClick={onSaveDefaultIntro}
                className="gap-1 text-gray-500 enabled:hover:text-blue-700"
                icon={<MdStarBorder size={14} />}
                title={
                  t(
                    "dashboard.entities.viewEntityUser.sendMsg.setAsDefault"
                  ) as string
                }
              />
            </div>
          )}
        </section>
      )}

      <section className="py-6 first:pt-0 last:pb-0">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900">
            {t(
              "dashboard.entities.viewEntityUser.sendMsg.sections.messageContent"
            )}
          </h3>

          {onCorrect && (
            <CustomButton
              variant="secondary"
              size="sm"
              onClick={onCorrect}
              disabled={
                isCorrectingMessage ||
                isContentUndefined ||
                (contentEditorRef.current ? editorIsEmpty : false)
              }
              isLoading={isCorrectingMessage}
              tooltip={
                t(
                  "dashboard.entities.viewEntityUser.sendMsg.aiImprove.label"
                ) as string
              }
              comp={
                <div className="flex items-center gap-2">
                  <MdAutoAwesome size={16} />
                  <span className="hidden sm:inline">
                    {isCorrectingMessage
                      ? t(
                          "dashboard.entities.viewEntityUser.sendMsg.aiImprove.processing"
                        )
                      : t(
                          "dashboard.entities.viewEntityUser.sendMsg.aiImprove.label"
                        )}
                  </span>
                </div>
              }
            />
          )}
        </div>

        <div
          translate="no"
          className="rounded-lg border border-solid border-gray-300 bg-white"
        >
          <Controller
            name="content"
            control={control}
            defaultValue=""
            rules={{
              required: '"Message Content" is not allowed to be empty',
              validate: (value) =>
                !isHtmlEmpty(value || "") ||
                '"Message Content" is not allowed to be empty',
            }}
            render={({ field: { onChange, value } }) => (
              <HtmlEditor
                ref={contentEditorRef}
                value={value}
                onChange={(content) => {
                  const empty = isHtmlEmpty(content || "");
                  onChange(empty ? "" : content);
                }}
                placeholder={
                  t(
                    "dashboard.entities.viewEntityUser.sendMsg.inp2Placeholder"
                  ) as string
                }
                minHeight="300px"
                maxHeight="500px"
                className="border-gray-300"
                maxLength={RICH_TEXT_LIMITS.messageContent}
                onValidationChange={handleEditorValidation}
                showCharacterCount
              />
            )}
          />
        </div>

        {contentProblem && (
          <p className="mt-2 text-sm font-medium text-red-600">
            {contentProblem}
          </p>
        )}
      </section>

      <section className="py-6 first:pt-0 last:pb-0">
        <h3 className="mb-3 flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900">
          {t("dashboard.entities.viewEntityUser.sendMsg.sections.attachments")}
          <span className="text-sm font-normal text-gray-400">
            {t("dashboard.entities.viewEntityUser.sendMsg.optional")}
          </span>
        </h3>

        <div className="grid gap-4">
          <input
            ref={attachmentInputRef}
            type="file"
            multiple
            accept={ACCEPTED_FILE_TYPES}
            onChange={onFileChange}
            hidden
          />

          {attachments.length < 5 && (
            <div>
              <CustomButton
                variant="secondary"
                size="sm"
                onClick={onTriggerFileSelection}
                icon="paperClip"
                title={
                  t(
                    "dashboard.entities.viewEntityUser.sendMsg.buttons.addFiles"
                  ) as string
                }
              />
            </div>
          )}

          {attachments.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">
                {t("dashboard.entities.viewEntityUser.sendMsg.attachedFiles", {
                  count: attachments.length,
                })}
              </p>
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                {attachments.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between p-3 transition-colors duration-150 hover:bg-gray-50"
                  >
                    <div className="flex min-w-0 flex-1 items-center">
                      <div className="mr-3 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                        <MdAttachFile className="text-gray-500" size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p
                          className="hidden truncate text-sm font-medium text-gray-900 md:block"
                          title={file.name}
                        >
                          {file.name.length > 35
                            ? `...${file.name.slice(-35)}`
                            : file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemoveFile(index)}
                      className="group ml-3 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white transition-colors duration-150 hover:border-red-200 hover:bg-red-50"
                      title={
                        t(
                          "dashboard.entities.viewEntityUser.sendMsg.buttons.removeFile"
                        ) as string
                      }
                    >
                      <MdClose
                        className="text-gray-400 group-hover:text-red-600"
                        size={16}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ComposeTab;
